import {
	Box,
	Circle,
	Flex,
	SimpleGrid,
	Text,
	useColorModeValue,
} from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React from "react";

interface TemplateCardProps {
	title: string;
	children: React.ReactNode;
	onClick?: () => void;
}

export const TemplateCard: React.FC<TemplateCardProps> = ({
	title,
	children,
	onClick,
}) => {
	const bgColor = useColorModeValue("white", "gray.800");
	const shadowColor = useColorModeValue(
		"rgba(0, 0, 0, 0.05)",
		"rgba(0, 0, 0, 0.3)"
	);

	return (
		<Box
			borderRadius="md"
			overflow="hidden"
			boxShadow={`0 2px 8px ${shadowColor}`}
			border="1px solid #F4F4F4"
			display="flex"
			flexDirection="column"
			cursor="pointer"
			onClick={onClick}
			_hover={{ boxShadow: `0 4px 12px ${shadowColor}` }}
		>
			<Box
				bg={bgColor}
				flex="1"
				display="flex"
				alignItems="center"
				justifyContent="center"
				minH="180px"
				backgroundColor="#FCFAFF"
			>
				{children}
			</Box>
			<Box p={3} borderTopWidth="1px" borderColor="gray.100">
				<Text fontSize="sm" fontWeight="medium" textAlign="center">
					{title}
				</Text>
			</Box>
		</Box>
	);
};

const HeaderCard = () => (
	<Box position="relative" h="full" w="full">
		<Box
			position="absolute"
			top={0}
			left={0}
			right={0}
			height="32px"
			bg="purple.100"
			borderTopLeftRadius="md"
			borderTopRightRadius="md"
			px={2}
			display="flex"
			alignItems="center"
		>
			<Box w="8px" h="8px" borderRadius="full" bg="#fff" mr={1} />
			<Box w="8px" h="8px" borderRadius="full" bg="#fff" mr={1} />
			<Box w="8px" h="8px" borderRadius="full" bg="#fff" mr="auto" />

			<Box w="12px" h="4px" bg="#fff" mx={1} />
			<Box w="12px" h="4px" bg="#fff" mx={1} />
			<Box w="12px" h="4px" bg="#fff" mx={1} />
			<Box w="12px" h="4px" bg="#fff" mx={1} />
		</Box>

		<Flex
			h="full"
			w="full"
			alignItems="center"
			justifyContent="center"
			pt="32px"
		>
			{/* Empty flex container - no pointer icon */}
		</Flex>
	</Box>
);

const FooterCard = () => (
	<Box
		position="relative"
		h="full"
		w="full"
		display="flex"
		alignItems="flex-end"
		justifyContent="center"
	>
		<Box
			position="absolute"
			bottom={0}
			left={0}
			right={0}
			height="32px"
			bg="purple.100"
			px={4}
			py={2}
			display="flex"
			alignItems="center"
			justifyContent="space-between"
		>
			<Box w="32px" h="6px" bg="#fff" />

			<Flex>
				<Box w="20px" h="6px" bg="#fff" mx={1} />
				<Box w="20px" h="6px" bg="#fff" mx={1} />
				<Box w="20px" h="6px" bg="#fff" mx={1} />
				<Box w="20px" h="6px" bg="#fff" mx={1} />
			</Flex>
		</Box>
	</Box>
);

const SinglePostCard = () => (
	<Box position="relative" h="full" w="full" p={4}>
		<Flex
			direction="column"
			h="full"
			alignItems="center"
			justifyContent="center"
			position="relative"
			maxW="150px"
			mx="auto"
		>
			<Box w="150px" h="60px" bg="purple.100" position="relative" />
			<Box w="100%" mt={4} display="flex" alignItems="center">
				<Circle
					size="8px"
					bg="purple.100"
					color="purple.100"
					display="flex"
					alignItems="center"
					justifyContent="center"
					boxShadow="sm"
					marginRight={2}
				/>
				<Box w="20%" h="2px" bg="purple.100" />
			</Box>
			<Box w="100%" mt={2}>
				<Box w="100%" h="6px" bg="purple.100" mb={2} />
				<Box w="100%" h="6px" bg="purple.100" mb={2} />
				<Box w="80%" h="6px" bg="purple.100" />
			</Box>
		</Flex>
	</Box>
);

const ArchiveCard = () => (
	<SimpleGrid columns={2} spacing={3} h="full" w="full" p={4}>
		<Box>
			<Box h="40px" bg="purple.100" mb={2} />
			<Box h="6px" bg="purple.100" mb={1} />
			<Box h="6px" bg="purple.100" w="80%" />
		</Box>
		<Box>
			<Box h="40px" bg="purple.100" mb={2} />
			<Box h="6px" bg="purple.100" mb={1} />
			<Box h="6px" bg="purple.100" w="80%" />
		</Box>
		<Box>
			<Box h="40px" bg="purple.100" mb={2} />
			<Box h="6px" bg="purple.100" mb={1} />
			<Box h="6px" bg="purple.100" w="80%" />
		</Box>
		<Box>
			<Box h="40px" bg="purple.100" mb={2} />
			<Box h="6px" bg="purple.100" mb={1} />
			<Box h="6px" bg="purple.100" w="80%" />
		</Box>
	</SimpleGrid>
);

const NotFoundCard = () => (
	<Flex
		direction="column"
		alignItems="center"
		justifyContent="center"
		h="full"
		w="full"
	>
		<svg
			width="46"
			height="46"
			viewBox="0 0 46 46"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				//eslint-disable-next-line
				d="M23.2927 4.19702C25.2147 4.19702 26.9939 5.17587 28.0122 6.75029L28.2057 7.07345L41.5451 30.0886L41.6616 30.3253C41.9949 31.1495 42.1284 32.0408 42.0514 32.9264C41.9744 33.812 41.689 34.6669 41.2186 35.4212C40.7481 36.1754 40.1059 36.8077 39.3443 37.2664C38.5828 37.7251 37.7236 37.9971 36.8369 38.0603L36.463 38.0753H10.16L10.0679 38.0697L9.85751 38.0734C9.0207 38.0304 8.20402 37.8013 7.46691 37.4028C6.72979 37.0044 6.0908 36.4466 5.59642 35.7701L5.39163 35.4694C4.94418 34.7665 4.65819 33.973 4.55423 33.1462C4.45027 32.3194 4.53093 31.4798 4.79042 30.6879L4.92569 30.3234L5.03842 30.0886L18.3628 7.10539C18.8501 6.22429 19.5646 5.48982 20.4319 4.97835C21.2992 4.46688 22.2877 4.19709 23.2946 4.19702H23.2927ZM23.3303 26.8026L23.0917 26.8158C22.6351 26.8701 22.2142 27.09 21.9088 27.4338C21.6035 27.7776 21.4348 28.2215 21.4348 28.6814C21.4348 29.1412 21.6035 29.5851 21.9088 29.929C22.2142 30.2728 22.6351 30.4927 23.0917 30.547L23.3115 30.5602L23.5501 30.547C24.0068 30.4927 24.4276 30.2728 24.733 29.929C25.0383 29.5851 25.207 29.1412 25.207 28.6814C25.207 28.2215 25.0383 27.7776 24.733 27.4338C24.4276 27.09 24.0068 26.8701 23.5501 26.8158L23.3303 26.8026ZM23.3115 15.5299C22.8513 15.5299 22.4072 15.6989 22.0633 16.0047C21.7194 16.3105 21.4997 16.7318 21.4459 17.1888L21.4327 17.4087V21.1662L21.4459 21.3861C21.5002 21.8427 21.7201 22.2636 22.0639 22.5689C22.4078 22.8743 22.8517 23.0429 23.3115 23.0429C23.7714 23.0429 24.2153 22.8743 24.5591 22.5689C24.9029 22.2636 25.1228 21.8427 25.1771 21.3861L25.1903 21.1662V17.4087L25.1771 17.1888C25.1233 16.7318 24.9036 16.3105 24.5597 16.0047C24.2158 15.6989 23.7717 15.5299 23.3115 15.5299Z"
				fill="#ECE2FD"
			/>
		</svg>
		<Text color="purple.300" fontSize="sm">
			{__("404 Page Not Found", "magazine-blocks")}
		</Text>
	</Flex>
);

const SearchResultCard = () => (
	<Flex
		direction="column"
		alignItems="center"
		justifyContent="center"
		h="full"
		w="full"
	>
		<svg
			width="46"
			height="46"
			viewBox="0 0 46 46"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				//eslint-disable-next-line
				d="M26.4204 5.805C28.2063 6.83615 29.7604 8.22447 30.9856 9.88325C32.2109 11.542 33.0809 13.4356 33.5414 15.4458C34.0018 17.4559 34.0429 19.5394 33.6619 21.5661C33.281 23.5928 32.4862 25.5192 31.3273 27.225L40.5973 36.4931C41.1134 37.0017 41.4133 37.6898 41.4345 38.4141C41.4557 39.1383 41.1966 39.8428 40.7112 40.3807C40.2257 40.9186 39.5514 40.7484 38.8288 41.3014C38.1062 41.3544 37.391 41.1264 36.8323 40.665L36.6185 40.4719L27.3504 31.2019C25.133 32.708 22.5525 33.5925 19.8774 33.7632C17.2023 33.9339 14.5303 33.3847 12.1394 32.1726C9.74857 30.9605 7.72619 29.1299 6.28271 26.8712C4.83923 24.6125 4.02739 22.0082 3.93166 19.3294L3.92041 18.795L3.92979 18.2606C4.02202 15.6745 4.78173 13.1563 6.13506 10.9506C7.48839 8.74497 9.38931 6.92694 11.6531 5.67324C13.9168 4.41954 16.4664 3.7728 19.0541 3.79589C21.6417 3.81897 24.1794 4.5111 26.4204 5.805Z"
				fill="#ECE2FD"
			/>
		</svg>
		<Text color="purple.300" fontSize="sm" mt={2}>
			{__("Search Result", "magazine-blocks")}
		</Text>
	</Flex>
);

// Template card components mapping
export const templateCards = [
	{ title: "Header", slug: "header", component: <HeaderCard /> },
	{ title: "Footer", slug: "footer", component: <FooterCard /> },
	{ title: "Single Post", slug: "single", component: <SinglePostCard /> },
	{ title: "Archive", slug: "archive", component: <ArchiveCard /> },
	{ title: "404 Page", slug: "404", component: <NotFoundCard /> },
	{ title: "Search Result", slug: "search", component: <SearchResultCard /> },
];
